import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { HashRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar/Navbar';
import TrendingCompanies from './components/TrendingCompanies/TrendingCompanies';
import JobPositions from './components/JobPositions/JobPositions';
import Filters from './components/Filters/Filters';
import QuestionsList from './components/QuestionsList/QuestionsList';
import PracticeQuestion from './components/PracticeQuestion/PracticeQuestion';
import Footer from './components/Footer/Footer';
import PremiumPage from './components/PremiumPage/PremiumPage';
import Register from './components/Register/Register';
import SignIn from './components/SignIn/SignIn';
import PasswordReset from './components/PasswordReset/PasswordReset';
import ChangePassword from './components/ChangePassword/ChangePassword';
import FAQ from './components/FAQ/FAQ';
import Homepage from './components/Homepage/Homepage';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import EmailVerification from './components/EmailVerification/EmailVerification'; // Adjust the path as necessary
import MyLists from './components/MyLists/MyLists';
import MySubmissions from './components/MySubmissions/MySubmissions';
import SubmissionReview from './components/SubmissionReview/SubmissionReview';
import NoOrder from './components/NoOrder/NoOrder';
import Terms from './components/Terms/Terms';
import PageTracker from './hooks/PageTracker';

export const AuthContext = createContext({
  isSignedIn: false,
  isPremiumUser: false,
  handleSignOut: () => { },
  handleSignIn: () => { },
  setPremiumStatus: () => { },
});

// Modify the AxiosInterceptorWrapper to use the context for sign-out
const AxiosInterceptorWrapper = () => {
  const navigate = useNavigate();
  const { handleSignOut } = useContext(AuthContext);

  useEffect(() => {
    const setupAxiosInterceptors = () => {
      const onResponseFailure = (error) => {
        if (error.response && error.response.status === 401) {
          handleSignOut();
          navigate('/signin');
        }
        return Promise.reject(error);
      };

      axios.interceptors.response.use((response) => response, onResponseFailure);
    };

    setupAxiosInterceptors();
  }, [navigate, handleSignOut]);

  return null; // This component does not render anything
};

function App() {
  const [currentFilters, setCurrentFilters] = useState({});
  const [selectedCompanyFromTrending, setSelectedCompanyFromTrending] = useState('');
  const [isSignedIn, setIsSignedIn] = useState(localStorage.getItem('isSignedIn') === 'true');
  const [isPremiumUser, setIsPremiumUser] = useState(localStorage.getItem('isPremiumUser') === 'true');


  const handleFilterChange = (newFilters) => {
    setCurrentFilters(prev => ({
      ...prev,
      ...newFilters,
      pageResetIndicator: (prev.pageResetIndicator || 0) + 1 // Increment counter
    }));
  };

  const resetFilters = () => {
    setCurrentFilters({});
    setSelectedCompanyFromTrending(''); // Ensure this is added to clear the selection
  };

  const handleSignIn = () => {
    setIsSignedIn(true);
    localStorage.setItem('isSignedIn', 'true');
  };

  const handleSignOut = () => {
    setIsSignedIn(false);
    setIsPremiumUser(false); // Ensure premium status is reset
    localStorage.removeItem('isSignedIn');
    localStorage.removeItem('isPremiumUser'); // Clear premium status from local storage
  };

  const setPremiumStatus = (status) => {
    setIsPremiumUser(status);
    localStorage.setItem('isPremiumUser', status.toString());
  };
  useEffect(() => {
    if (localStorage.getItem('isSignedIn') === 'true') {
      fetchPremiumStatus();
    }
  }, [isSignedIn]);

  const fetchPremiumStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get-premium-status`, { withCredentials: true });
      if (response.data.isPremiumUser !== undefined) {
        setIsPremiumUser(response.data.isPremiumUser); // Directly set state
        localStorage.setItem('isPremiumUser', response.data.isPremiumUser.toString()); // Sync local storage
      }
    } catch (error) {
      console.error('Error fetching premium status:', error);
    }
  };

  // Define the value for the context provider
  const authProviderValue = {
    isSignedIn,
    isPremiumUser,
    handleSignOut,
    handleSignIn,
    setPremiumStatus,
  };

  return (
    <AuthContext.Provider value={authProviderValue}>
      <Router>
        <PageTracker />
        <AxiosInterceptorWrapper /> {/* This component sets up the Axios interceptor */}
        <div className="app-container">
          <Navbar />
          <Routes>
            <Route path="/" element={<Homepage />} /> {/* Homepage route */}
            <Route path="/interview-questions" element={ /* Interview Questions route */
              <>
                <div className="content-container">
                  <div className="left-container">
                    <JobPositions />
                    <Filters onFilterChange={handleFilterChange} resetFilters={resetFilters} />
                    <QuestionsList filters={currentFilters} selectedCompanyFromTrending={selectedCompanyFromTrending} />
                  </div>
                  <TrendingCompanies setSelectedCompanyFromTrending={setSelectedCompanyFromTrending} />

                </div>
              </>
            } />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signin" element={<SignIn onSignIn={handleSignIn} />} />
            <Route path="/verify" element={<EmailVerification />} />
            <Route path="/" element={<Homepage />} />
            <Route path="/practice-question/:id" element={<PracticeQuestion />} />
            <Route path="/forgot-password" element={<PasswordReset />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/my-lists" element={<MyLists />} />
            <Route path="/my-submissions" element={<MySubmissions />} />
            <Route path="/submission-review/:id" element={<SubmissionReview />} />
            <Route path="/no-order" element={<NoOrder />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
          <Footer />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;

